// components/LibraryCardContent.js

import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import Barcode from "react-barcode";
import { sessionManager } from "@authentication/services/SessionManager";

const getCourseDeptDisplay = (user) => {
  const program = user?.program || "";
  const isLong = program.length > 40; // tweak threshold for "too long"

  const style = {
    fontSize: 18,
    lineHeight: "18px",
    fontWeight: "500",
    marginTop: isLong ? "1px" : "0px", // only COURSE/DEPT. gets this
  };

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: "2px" }}>
      {program && <div style={style}>{program}</div>}
    </div>
  );
};

const LibraryCardContent = forwardRef(
  (
    { user, STYLES, renderLogo, renderPhoto, librarianName, librarianTitle },
    ref
  ) => {
    // Get library ID from sessionManager or fallback
    const libraryId =
      sessionManager.isAuthenticated() && sessionManager.user?.library_id
        ? sessionManager.user.library_id
        : "F25-55";

    return (
      <div ref={ref}>
        <div style={STYLES.header}>
          {renderLogo(
            `${process.env.PUBLIC_URL}/images/university-logo.png`,
            "University Logo",
            STYLES.leftLogo,
            (e) => {
              console.warn("University logo failed to load");
              e.target.style.display = "none";
            }
          )}

          <div style={STYLES.universityInfo}>
            <h1 style={STYLES.universityName}>ROMBLON STATE UNIVERSITY</h1>
            <p style={STYLES.universityLocation}>Odiongan, Romblon</p>
            <h2 style={STYLES.cardTitle}>STUDENT LIBRARY CARD</h2>
          </div>

          {renderLogo(
            `${process.env.PUBLIC_URL}/images/lrc-logo.png`,
            "Library Logo",
            STYLES.rightLogo,
            (e) => {
              console.warn("Library logo failed to load");
              e.target.style.display = "none";
            }
          )}
        </div>

        <div style={STYLES.mainContent}>
          <div style={STYLES.leftSection}>
            <div style={STYLES.fieldGroup}>
              <div style={STYLES.fieldLabel}>COURSE/DEPT.</div>
              <div
                style={{
                  ...STYLES.fieldLine,
                  height: "auto", // allow it to grow
                  alignItems: "flex-start", // so margin-top applies
                }}
              >
                <div style={STYLES.fieldValue}>
                  {getCourseDeptDisplay(user)}
                </div>
              </div>
            </div>

            <div style={STYLES.fieldGroup}>
              <div style={STYLES.fieldLabel}>FULLNAME</div>
              <div style={STYLES.fieldLine}>
                <div style={STYLES.fieldValue}>
                  {user.full_name || user.fullName || ""}
                </div>
              </div>
            </div>

            <div style={STYLES.fieldGroup}>
              <div style={STYLES.fieldLabel}>SIGNATURE</div>
              <div style={STYLES.fieldLine}>
                <div style={STYLES.fieldValue}>{user.signature || ""}</div>
              </div>
            </div>

            {/* Barcode using dynamic library ID */}
            <Barcode
              value={user.library_id || libraryId}
              height={40}
              width={3}
              style={STYLES.barcode}
            />
          </div>

          <div style={STYLES.rightSection}>
            <div style={STYLES.photoContainer}>{renderPhoto()}</div>

            <div style={STYLES.librarianSection}>
              <div style={STYLES.fieldLine}>
                <div
                  style={{
                    ...STYLES.librarianName,
                    width: "100%",
                    textAlign: "center",
                    fontSize: 14,
                  }}
                >
                  {librarianName.toUpperCase()}
                </div>
              </div>
              <p style={STYLES.librarianTitle}>{librarianTitle}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

LibraryCardContent.displayName = "LibraryCardContent";

LibraryCardContent.propTypes = {
  user: PropTypes.object.isRequired,
  STYLES: PropTypes.object.isRequired,
  renderLogo: PropTypes.func.isRequired,
  renderPhoto: PropTypes.func.isRequired,
  librarianName: PropTypes.string.isRequired,
  librarianTitle: PropTypes.string.isRequired,
};

export default LibraryCardContent;
